import { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { ExportButton } from "@/pages/Report";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Save, CheckCircle2, ChevronLeft, ChevronRight, PenLine, Loader2, Trash2, AlertTriangle } from "lucide-react";
import { AIInsightCard } from "@/components/AIChat";

const CATEGORIA_LABELS = {
  reception: "Reception", camere: "Camere", housekeeping: "Housekeeping",
  colazione: "Colazione", hall: "Hall", aree_comuni: "Aree comuni",
  bagni: "Bagni", aree_esterne: "Aree esterne", presentazione_personale: "Personale",
  back_office: "Back office", locali_tecnici: "Locali tecnici",
};

const classify = (p) => p == null ? null : (p >= 80 ? "eccellente" : (p >= 60 ? "buono" : "critico"));
const valTone = { eccellente: "text-emerald-500", buono: "text-amber-500", critico: "text-rose-500" };
const valBg = { eccellente: "bg-emerald-500", buono: "bg-amber-500", critico: "bg-rose-500" };

export default function AuditDettaglio() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [ispezione, setAudit] = useState(null);
  const [hotel, setHotel] = useState(null);
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const saveTimer = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/audits/${id}`);
        setAudit(data);
        const h = await api.get(`/hotels/${data.hotel_id}`);
        setHotel(h.data);
      } catch { toast.error("Ispezione non trovata"); }
    })();
  }, [id]);

  const isReadOnly = ispezione?.stato === "completato";

  const updateCategoria = (idx, key, val) => {
    setAudit((a) => {
      const cs = a.categoria_scores.map((c, i) => i === idx ? { ...c, [key]: val } : c);
      return { ...a, categoria_scores: cs };
    });
    if (!isReadOnly) {
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => doSave(), 1200);
    }
  };

  const doSave = async () => {
    if (!ispezione || isReadOnly) return;
    setSaving(true);
    try {
      await api.patch(`/audits/${id}`, { categoria_scores: ispezione.categoria_scores, note_generali: ispezione.note_generali || "" });
      setLastSaved(new Date());
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const punteggio = useMemo(() => {
    if (!ispezione) return null;
    const valid = ispezione.categoria_scores.filter((c) => c.punteggio != null);
    if (valid.length === 0) return null;
    const avg10 = valid.reduce((s, c) => s + c.punteggio, 0) / valid.length;
    return { totale: Math.round(avg10 * 10 * 10) / 10, completi: valid.length };
  }, [ispezione]);

  const totalSteps = (ispezione?.categoria_scores.length || 11) + 1; // +1 riepilogo
  const isLast = step === totalSteps - 1;

  if (!ispezione) return <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Caricamento…</div>;

  const complete = async () => {
    setSubmitting(true);
    try {
      await api.patch(`/audits/${id}/complete`, { note_generali: ispezione.note_generali || "" });
      toast.success("Ispezione completata e firmata");
      setConfirmOpen(false);
      navigate("/ispezioni");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmitting(false); }
  };

  const cat = ispezione.categoria_scores[step];
  const valTotale = classify(punteggio?.totale);
  const allValid = ispezione.categoria_scores.every((c) => c.punteggio != null);

  return (
    <div className="ih-anim-in" data-testid="audit-dettaglio">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <Button variant="ghost" onClick={() => navigate(-1)} className="gap-1"><ArrowLeft className="h-4 w-4" /> Indietro</Button>
        <div className="flex items-center gap-3">
          {user?.role === "super_admin" && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDeleteOpen(true)}
              className="text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/40 border-rose-300/60"
              data-testid="audit-delete-btn"
            >
              <Trash2 className="h-3.5 w-3.5 mr-1" /> Elimina
            </Button>
          )}
          {ispezione.stato === "completato" && (
            <>
              <ExportButton url={`/reports-export/audit/${id}?format=pdf`} filename={`ih-auditflow-ispezione-${id.slice(0, 8)}.pdf`} label="PDF" />
              <ExportButton url={`/reports-export/audit/${id}?format=xlsx`} filename={`ih-auditflow-ispezione-${id.slice(0, 8)}.xlsx`} label="Excel" />
            </>
          )}
          {saving ? <span className="text-xs text-muted-foreground flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Salvataggio…</span>
            : lastSaved && <span className="text-xs text-muted-foreground">Salvato {Math.round((Date.now() - lastSaved) / 1000)}s fa</span>}
          {punteggio && (
            <div className={`px-3 py-1.5 rounded-xl ih-glass font-display text-lg ${valTone[valTotale] || ""}`} data-testid="audit-live-score">
              {punteggio.totale}<span className="text-xs text-muted-foreground">/100</span>
              <span className="text-[10px] ml-2 uppercase tracking-wider">{punteggio.completi}/11</span>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6">
        {/* Stepper */}
        <div className="ih-glass rounded-2xl p-3 space-y-1 h-fit sticky top-20">
          <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground p-2 mb-1">Categorie</div>
          {ispezione.categoria_scores.map((c, i) => {
            const done = c.punteggio != null;
            return (
              <button key={c.categoria} data-testid={`step-${c.categoria}`} onClick={() => setStep(i)} className={`w-full text-left flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${step === i ? "bg-accent/70" : "hover:bg-accent/30"}`}>
                <div className={`h-5 w-5 rounded-full flex items-center justify-center text-[10px] font-bold ${done ? "bg-emerald-500 text-white" : "bg-muted text-muted-foreground"}`}>{done ? "✓" : i + 1}</div>
                <span className="truncate">{CATEGORIA_LABELS[c.categoria]}</span>
              </button>
            );
          })}
          <button data-testid="step-riepilogo" onClick={() => setStep(totalSteps - 1)} className={`w-full text-left flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors mt-2 border-t border-border/40 pt-3 ${step === totalSteps - 1 ? "bg-accent/70 font-semibold" : "hover:bg-accent/30"}`}>
            <div className="h-5 w-5 rounded-full bg-lavender-500 text-white flex items-center justify-center text-[10px] font-bold">∑</div>
            <span>Riepilogo</span>
          </button>
        </div>

        {/* Step content */}
        <div className="space-y-4">
          {!isLast && cat ? (
            <div className="ih-glass rounded-2xl p-6">
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-1">Step {step + 1}/{totalSteps} · {hotel?.nome}</div>
              <h2 className="font-display text-2xl tracking-tight mb-4">{CATEGORIA_LABELS[cat.categoria]}</h2>

              <div className="space-y-2 mb-5">
                {cat.checklist_results.map((it, i) => (
                  <div key={it.item_id} className="rounded-xl border border-border/60 p-3 bg-background/40">
                    <div className="text-sm font-medium mb-2">{it.label}</div>
                    <div className="flex gap-2">
                      {[["ok", "Conforme", "bg-emerald-500"], ["parziale", "Parziale", "bg-amber-500"], ["ko", "Non conforme", "bg-rose-500"]].map(([v, label, bg]) => (
                        <button
                          key={v}
                          disabled={isReadOnly}
                          onClick={() => {
                            const newList = cat.checklist_results.map((x, j) => j === i ? { ...x, valore: v } : x);
                            updateCategoria(step, "checklist_results", newList);
                          }}
                          className={`text-xs px-2.5 py-1 rounded-md border transition-colors ${it.valore === v ? `${bg} text-white border-transparent` : "bg-background/60 border-border/60 text-muted-foreground hover:bg-accent/40"}`}
                        >{label}</button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="space-y-3 pt-3 border-t border-border/40">
                <div>
                  <label className="text-xs uppercase tracking-[0.14em] text-muted-foreground mb-2 block">Punteggio categoria (1-10)</label>
                  <div className="flex items-center gap-3">
                    <Input type="range" min="1" max="10" step="0.5" disabled={isReadOnly} value={cat.punteggio || 5} onChange={(e) => updateCategoria(step, "punteggio", parseFloat(e.target.value))} data-testid={`punteggio-${cat.categoria}`} className="flex-1" />
                    <div className={`text-2xl font-display w-16 text-center font-medium ${valTone[classify((cat.punteggio || 0) * 10)] || ""}`}>{cat.punteggio != null ? cat.punteggio.toFixed(1) : "—"}</div>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1">1-3 critico · 4-6 sufficiente · 7-8 buono · 9-10 eccellente</p>
                </div>
                <Textarea disabled={isReadOnly} value={cat.commenti || ""} onChange={(e) => updateCategoria(step, "commenti", e.target.value)} placeholder="Commenti categoria" className="bg-background/40 min-h-[80px]" data-testid={`commenti-${cat.categoria}`} />
              </div>
            </div>
          ) : (
            <div className="ih-glass rounded-2xl p-6">
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-1">Riepilogo finale</div>
              <h2 className="font-display text-2xl tracking-tight mb-4">Risultato ispezione</h2>
              <div className="flex items-center gap-6 mb-6">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1">Punteggio totale</div>
                  <div className={`font-display text-6xl tracking-tight font-medium ${valTone[valTotale] || ""}`}>{punteggio?.totale ?? "—"}<span className="text-2xl text-muted-foreground">/100</span></div>
                </div>
                {valTotale && (
                  <div className={`px-3 py-1 rounded-full text-white text-sm font-semibold uppercase tracking-wider ${valBg[valTotale]}`}>{valTotale}</div>
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 mb-5">
                {ispezione.categoria_scores.map((c) => (
                  <div key={c.categoria} className="rounded-lg border border-border/40 p-2 text-xs">
                    <div className="text-muted-foreground">{CATEGORIA_LABELS[c.categoria]}</div>
                    <div className="font-medium">{c.punteggio != null ? c.punteggio.toFixed(1) : "—"}/10</div>
                  </div>
                ))}
              </div>
              <Textarea disabled={isReadOnly} value={ispezione.note_generali || ""} onChange={(e) => setAudit((a) => ({ ...a, note_generali: e.target.value }))} onBlur={doSave} placeholder="Note generali sull'audit" className="bg-background/40 min-h-[100px]" data-testid="note-generali" />
              {ispezione.firma && (
                <div className="mt-5 rounded-xl bg-accent/40 p-4 text-sm">
                  <div className="flex items-center gap-2 mb-1"><PenLine className="h-4 w-4 text-lavender-500" /> Firmato da <span className="font-semibold">{ispezione.firma.nome_cognome}</span></div>
                  <div className="text-xs text-muted-foreground">il {new Date(ispezione.firma.timestamp).toLocaleString("it-IT")}</div>
                  {ispezione.prossimo_audit_date && <div className="text-xs text-muted-foreground mt-1">Prossima ispezione prevista: {new Date(ispezione.prossimo_audit_date).toLocaleDateString("it-IT")}</div>}
                </div>
              )}
              {ispezione.stato === "completato" && (
                <div className="mt-5">
                  <AIInsightCard
                    endpoint={`/ai/ispezione-summary/${id}`}
                    params={{}}
                    title="Executive summary AI"
                    sections={["key_strengths", "key_weaknesses", "recommendations"]}
                  />
                </div>
              )}
            </div>
          )}

          {!isReadOnly && (
            <div className="ih-glass-strong rounded-2xl p-4 flex items-center justify-between gap-3">
              <Button variant="outline" disabled={step === 0} onClick={() => setStep((s) => s - 1)}><ChevronLeft className="h-4 w-4 mr-1" /> Indietro</Button>
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={doSave} disabled={saving}><Save className="h-3 w-3 mr-1" /> Salva bozza</Button>
                {isLast ? (
                  <Button data-testid="audit-complete-btn" disabled={!allValid} onClick={() => setConfirmOpen(true)} className="gap-1"><CheckCircle2 className="h-4 w-4" /> Completa audit</Button>
                ) : (
                  <Button onClick={() => setStep((s) => s + 1)}>Avanti <ChevronRight className="h-4 w-4 ml-1" /></Button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="ih-glass-strong">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Confermi la finalizzazione?</DialogTitle>
            <DialogDescription>L'ispezione verrà firmata digitalmente. L'azione è irreversibile.</DialogDescription>
          </DialogHeader>
          <div className="rounded-xl bg-accent/40 p-4 text-sm">
            <div className="flex items-center gap-2"><PenLine className="h-4 w-4 text-lavender-500" /> Firmato da <span className="font-semibold">{user?.nome} {user?.cognome}</span></div>
            <div className="text-xs text-muted-foreground mt-1">Punteggio: <strong>{punteggio?.totale}/100</strong> · {valTotale}</div>
            <div className="text-xs text-muted-foreground">Prossima ispezione calcolata: tra 60 giorni</div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>Annulla</Button>
            <Button data-testid="confirm-complete-audit" onClick={complete} disabled={submitting}>{submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Firma e completa"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete modal (SA only, v99+) */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="ih-glass-strong sm:max-w-md" data-testid="audit-delete-modal">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight flex items-center gap-2 text-rose-600">
              <AlertTriangle className="h-5 w-5" />
              Eliminare l'ispezione?
            </DialogTitle>
            <DialogDescription>
              Stai per eliminare l'ispezione <strong className="text-foreground">{hotel?.nome} · {ispezione?.data_prevista}</strong>. Questa operazione è <strong>irreversibile</strong> — foto, checklist e commenti andranno persi.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>Annulla</Button>
            <Button
              data-testid="audit-delete-confirm"
              disabled={deleting}
              onClick={async () => {
                setDeleting(true);
                try {
                  await api.delete(`/audits/${id}`);
                  toast.success("Ispezione eliminata");
                  setDeleteOpen(false);
                  navigate("/audit");
                } catch (e) {
                  toast.error(formatApiError(e));
                } finally { setDeleting(false); }
              }}
              className="bg-rose-600 hover:bg-rose-700 text-white gap-1.5"
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              Elimina definitivamente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
